import { CircularProgress, TablePagination } from '@material-ui/core';
import React, { useEffect, useState, useContext, useMemo } from 'react';

import { useStyles } from './styles';
import { IProductReturn } from './interfaces';
import { dateToLocalDateString } from 'utils/date';
import { TokVisionFiltersBar } from './components/tokvisionfiltersbar';
import { SupplierFiltersBar } from './components/supplierfiltersbar';
import { Can } from '../../../components/util/Can';
import { DeleteCell } from './components/deleteCell';
import { GetUserInfo } from 'components/util/Helpers/Auth';
import { AuthContext } from '../../../contexts/AuthContext';
import { useEnvironment, useSnackbar } from '../../../hooks';
import { IFiltersVariables } from './components/tokvisionfiltersbar/interfaces';
import { PicturesVisualizer } from './components/picturesVisualizer';
import { PageBarHeader } from '../../../components/migrated/pageheader';
import { IReturnReceipt } from './graphql/allReturnReceipts/interfaces';
import { useQualityReturnReceiptsGql } from './graphql/allReturnReceipts';
import { usePagination } from '../../../hooks/generictable/usePagination';
import { GenericTable } from '../../../components/migrated/generictable/';
import { ProductReturnVisualizer } from './components/productReturnVisualizer';
import ListaVazia from '../../../components/util/listaVazia/.mixins/ListaVazia';
import { ReturnReceiptReasonsCell } from './components/returnReceiptReasonsCell';
import { ColumnDefinitionType } from '../../../components/migrated/generictable/interfaces';

export const ProductReturn = ({ router }: IProductReturn) => {
  const {
    user: { isVisaoTok }
  } = useContext(AuthContext);
  const classes = useStyles();
  const { DEBUG } = useEnvironment();
  const { Snackbar, toggleSnackbar } = useSnackbar();

  const [filters, setFilters] = useState<IFiltersVariables>({
    personId: 0,
    initialRow: 0,
    nextRows: 5
  });

  const {
    runQualityReturnReceiptsGql,
    dataQualityReturnReceipts,
    errorQualityReturnReceipts,
    loadingQualityReturnReceipts
  } = useQualityReturnReceiptsGql();

  const {
    nextRows,
    initialRow,
    setTotalRows,
    handleChangePage,
    handleChangeRowsPerPage,
    nextRowsOptions,
    page,
    totalRows
  } = usePagination();

  const updateTableData = () => {
    runQualityReturnReceiptsGql({
      variables: {
        ...filters,
        receiptDate: filters.receiptDate ? dateToLocalDateString(filters.receiptDate) : null
      }
    });
  };

  const combinedColumns: ColumnDefinitionType<IReturnReceipt, keyof IReturnReceipt | any>[] = useMemo(() => {
    const tokVisionColumns = [
      {
        key: 'visualizeReturnReceipt',
        header: 'Visualizar relatório',
        customRender: (row: IReturnReceipt) => <ProductReturnVisualizer row={row} router={router} />
      },
      {
        key: 'productId',
        header: 'ID Prod'
      },
      {
        key: 'receiptDate',
        header: 'Dt. Recebimento'
      },
      {
        key: 'productName',
        header: 'Descrição',
        columnWidth: 400
      },
      {
        key: 'productDivisionName',
        header: 'Divisão'
      },
      {
        key: 'productGroupName',
        header: 'Grupo',
        columnWidth: 350
      },
      {
        key: 'returnReceiptReasons',
        header: 'Motivo(s) da Devolução',
        columnWidth: 540,
        customRender: (row: IReturnReceipt) => <ReturnReceiptReasonsCell row={row} />
      },
      {
        key: 'visualizeColumn',
        header: 'Visualizar fotos',
        customRender: (row: IReturnReceipt) => <PicturesVisualizer row={row} />
      },
      {
        key: 'delete',
        header: 'Excluir',
        visible: isVisaoTok,
        customRender: (row: IReturnReceipt) => <DeleteCell row={row} updateTableData={updateTableData} />
      }
    ];

    const defaultColumns = [
      {
        key: 'visualizeColumn',
        header: 'Visualizar fotos',
        customRender: (row: IReturnReceipt) => <PicturesVisualizer row={row} />
      },
      {
        key: 'productId',
        header: 'ID Prod'
      },
      {
        key: 'receiptDate',
        header: 'Dt. Recebimento'
      },
      {
        key: 'productName',
        header: 'Descrição',
        columnWidth: 400
      },
      {
        key: 'productDivisionName',
        header: 'Divisão'
      },
      {
        key: 'productGroupName',
        header: 'Grupo',
        columnWidth: 400
      },
      {
        key: 'returnReceiptReasons',
        header: 'Motivo(s) da Devolução',
        columnWidth: 510,
        customRender: (row: IReturnReceipt) => <ReturnReceiptReasonsCell row={row} />
      },
      {
        key: 'visualizeReturnReceipt',
        header: 'Visualizar relatório',
        customRender: (row: IReturnReceipt) => <ProductReturnVisualizer row={row} router={router} />
      }
    ];

    const columns = isVisaoTok ? tokVisionColumns : defaultColumns;
    return columns;
  }, [filters, isVisaoTok]);

  useEffect(() => {
    const routerPersonId = router.location?.state?.personId;

    if (routerPersonId) {
      setFilters((prev) => ({
        ...prev,
        routerPersonId: +routerPersonId
      }));
    }
  }, [router.location?.state?.personId]);

  useEffect(() => {
    if (dataQualityReturnReceipts && dataQualityReturnReceipts.length > 0) {
      setTotalRows(dataQualityReturnReceipts[0].totalRows);
    } else {
      setTotalRows(0);
    }
  }, [dataQualityReturnReceipts]);

  useEffect(() => {
    handleChangePage(null, 0);
  }, [
    filters.personId,
    filters.productDivisionIndicator,
    filters.productGroupId,
    filters.productId,
    filters.receiptDate
  ]);

  useEffect(() => {
    if (errorQualityReturnReceipts) {
      if (DEBUG === 'TRUE') {
        const msg = errorQualityReturnReceipts;
        console.log(`Erro ao carregar dados! Error:(message: ${msg} )`);
      } else {
        toggleSnackbar({
          message: 'Ocorreu um problema ao carregar informações. Por favor, informe ao suporte.',
          type: 'error'
        });
      }
    }
  }, [errorQualityReturnReceipts]);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      initialRow,
      nextRows
    }));
  }, [initialRow, nextRows]);

  useEffect(() => {
    if (filters.personId > 0) {
      runQualityReturnReceiptsGql({
        variables: {
          ...filters,
          productGroupId: filters.productGroupId ? +filters.productGroupId : undefined,
          productId: filters.productId ? +filters.productId : undefined,
          receiptDate: filters.receiptDate ? dateToLocalDateString(filters.receiptDate) : ''
        }
      });
    }
  }, [filters]);

  useEffect(() => {
    const setSupplier = async () => {
      if (!isVisaoTok) {
        const { id } = await GetUserInfo();
        setFilters((prev) => ({
          ...prev,
          personId: id
        }));
      }
    };
    setSupplier();
  }, []);

  return (
    <>
      <Snackbar />
      <PageBarHeader title="Devolução" />
      <div className={classes.container}>
        {isVisaoTok ? (
          <Can permissions={['filtro-fornecedor']}>
            <TokVisionFiltersBar
              filters={filters}
              setFilters={setFilters}
              onClickAdd={() => router.push('/qualidade/devolucao/novo-relatorio')}
            />
          </Can>
        ) : (
          <SupplierFiltersBar filters={filters} setFilters={setFilters} />
        )}

        {loadingQualityReturnReceipts && (
          <div className={classes.loader}>
            <CircularProgress />
          </div>
        )}

        {filters.personId > 0 && !loadingQualityReturnReceipts && dataQualityReturnReceipts.length > 0 && (
          <>
            <GenericTable columns={combinedColumns} data={dataQualityReturnReceipts} />
            <TablePagination
              component="div"
              count={totalRows}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={nextRows}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={nextRowsOptions}
              labelRowsPerPage="Registros por página"
              labelDisplayedRows={({ from, to, count }) => `Mostrando ${from}-${to} de ${count}`}
            />
          </>
        )}

        {!loadingQualityReturnReceipts && (
          <>
            {filters.personId > 0 && dataQualityReturnReceipts.length === 0 && (
              <ListaVazia
                tipoMensagem="Alert"
                text="Não encontramos devoluções"
                textDescription="Para o fornecedor selecionado"
              />
            )}
            {(filters.personId === 0 || isNaN(filters.personId)) && (
              <ListaVazia
                tipoMensagem="Search"
                text="Selecione o fornecedor"
                textDescription="Para relatórios de devolução"
              />
            )}
          </>
        )}
      </div>
    </>
  );
};
